/**
 * orgApi — typed client for the real M03 Organization / Employee / Territory /
 * Hierarchy backend (NestJS controllers under `@Controller('v1/...')`, reached
 * via the Vite `/api → /v1` proxy; screens call the `/v1/...` paths directly).
 *
 * This module centralises two things the earlier per-screen inline `apiRequest`
 * calls got wrong or duplicated:
 *  1. LIST-response unwrapping. Every m03 list service returns
 *     `{ data: T[], total }`, NOT a bare array. `getList` normalises both
 *     shapes so screens never crash on `.map`/`.filter` of an object.
 *  2. Field names + payload shapes that mirror the backend DTOs / entities
 *     exactly (see `backend/src/modules/m03-org/services/*` and `entities/*`).
 */
import { apiRequest } from './client.js';

type Tok = string | null | undefined;

/** Normalise a list response to `{ data, total }` regardless of backend shape. */
export async function getList<T>(path: string, token: Tok): Promise<{ data: T[]; total: number }> {
  const res = await apiRequest<unknown>(path, { token });
  if (Array.isArray(res)) return { data: res as T[], total: res.length };
  const obj = (res ?? {}) as { data?: T[]; items?: T[]; total?: number };
  // `{ items }` is the third real shape: AdminCompanyController, AdminThemeController,
  // AdminConfigController, AdminFeatureController, AdminStorageController and the
  // M08 vendor controllers all return it. Reading only `data` left every one of
  // those dropdowns silently empty instead of failing.
  const data = Array.isArray(obj.data)
    ? obj.data
    : Array.isArray(obj.items)
      ? obj.items
      : [];
  return { data, total: typeof obj.total === 'number' ? obj.total : data.length };
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// ── Enum value sets (for dropdowns) ──────────────────────────────────────────
export const RECORD_STATUSES = ['ACTIVE', 'INACTIVE', 'ARCHIVED'] as const;
export const EMPLOYEE_STATUSES = [
  'ACTIVE', 'INACTIVE', 'TRANSFERRED', 'SUSPENDED', 'RETIRED', 'RESIGNED', 'ARCHIVED', 'ON_LEAVE',
] as const;
export const REPORTING_TYPES = ['PRIMARY', 'SECONDARY'] as const;
export const MAPPING_TYPES = ['PRIMARY', 'SECONDARY'] as const;
export const ROLE_TYPES = ['PRIMARY', 'SECONDARY', 'TEMPORARY', 'DELEGATED'] as const;
export const STAKEHOLDER_ROLE_TYPES = [
  'SALES_OFFICER', 'SALES_HEAD', 'BRANDING_OFFICER', 'BRANDING_HEAD', 'ACCOUNTS_OFFICER', 'BACKUP',
] as const;
export const TRANSFER_TYPES = ['TERRITORY', 'MANAGER', 'DEPARTMENT', 'FULL'] as const;
export const SUCCESSOR_REASONS = ['RESIGNATION', 'RETIREMENT', 'PROMOTION', 'TRANSFER'] as const;

export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];
export type ReportingType = (typeof REPORTING_TYPES)[number];
export type MappingType = (typeof MAPPING_TYPES)[number];
export type RoleType = (typeof ROLE_TYPES)[number];
export type StakeholderRoleType = (typeof STAKEHOLDER_ROLE_TYPES)[number];
export type TransferType = (typeof TRANSFER_TYPES)[number];
export type SuccessorReason = (typeof SUCCESSOR_REASONS)[number];

// ── Organization (T028) ──────────────────────────────────────────────────────
export interface OrgRow {
  readonly orgId: string;
  readonly companyId: string | null;
  readonly orgCode: string;
  readonly orgName: string;
  readonly parentOrgId: string | null;
  readonly hierarchyLevel: number;
  readonly description: string | null;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
  readonly recordStatusCode: string;
}
export interface CreateOrgInput {
  companyId?: string;
  orgCode: string;
  orgName: string;
  parentOrgId?: string;
  hierarchyLevel?: number;
  description?: string;
  effectiveFromDate: string;
}
export const listOrgs = (token: Tok, search?: string) =>
  getList<OrgRow>(`/v1/org${qs({ search, limit: 200 })}`, token);
export const getOrg = (token: Tok, orgId: string) =>
  apiRequest<OrgRow>(`/v1/org/${orgId}`, { token });
export const createOrg = (token: Tok, body: CreateOrgInput) =>
  apiRequest<OrgRow>('/v1/org', { method: 'POST', token, body });
export const updateOrg = (token: Tok, orgId: string, body: Partial<CreateOrgInput>) =>
  apiRequest<OrgRow>(`/v1/org/${orgId}`, { method: 'PUT', token, body });
export const deactivateOrg = (token: Tok, orgId: string) =>
  apiRequest<OrgRow>(`/v1/org/${orgId}/deactivate`, { method: 'POST', token });

// ── Zone (T031) ──────────────────────────────────────────────────────────────
export interface ZoneRow {
  readonly zoneId: string;
  readonly companyId: string;
  readonly zoneCode: string;
  readonly zoneName: string;
  readonly zoneHeadId: string | null;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
  readonly recordStatusCode: string;
}
export interface CreateZoneInput {
  companyId: string;
  zoneCode: string;
  zoneName: string;
  zoneHeadId?: string;
  effectiveFromDate: string;
}
export const listZones = (token: Tok) => getList<ZoneRow>(`/v1/zones${qs({ limit: 200 })}`, token);
export const getZone = (token: Tok, id: string) => apiRequest<ZoneRow>(`/v1/zones/${id}`, { token });
export const createZone = (token: Tok, body: CreateZoneInput) =>
  apiRequest<ZoneRow>('/v1/zones', { method: 'POST', token, body });
export const updateZone = (token: Tok, id: string, body: Partial<CreateZoneInput>) =>
  apiRequest<ZoneRow>(`/v1/zones/${id}`, { method: 'PUT', token, body });
export const deactivateZone = (token: Tok, id: string) =>
  apiRequest<ZoneRow>(`/v1/zones/${id}/deactivate`, { method: 'POST', token });

// ── Region (T032) ────────────────────────────────────────────────────────────
export interface RegionRow {
  readonly regionId: string;
  readonly regionCode: string;
  readonly regionName: string;
  readonly parentZoneId: string;
  readonly regionHeadId: string | null;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
  readonly recordStatusCode: string;
}
export interface CreateRegionInput {
  regionCode: string;
  regionName: string;
  parentZoneId: string;
  regionHeadId?: string;
  effectiveFromDate: string;
}
export const listRegions = (token: Tok, zoneId?: string) =>
  getList<RegionRow>(`/v1/regions${qs({ zone_id: zoneId, limit: 200 })}`, token);
export const getRegion = (token: Tok, id: string) => apiRequest<RegionRow>(`/v1/regions/${id}`, { token });
export const createRegion = (token: Tok, body: CreateRegionInput) =>
  apiRequest<RegionRow>('/v1/regions', { method: 'POST', token, body });
export const updateRegion = (token: Tok, id: string, body: Partial<CreateRegionInput>) =>
  apiRequest<RegionRow>(`/v1/regions/${id}`, { method: 'PUT', token, body });
export const deactivateRegion = (token: Tok, id: string) =>
  apiRequest<RegionRow>(`/v1/regions/${id}/deactivate`, { method: 'POST', token });

// ── State (T033) ─────────────────────────────────────────────────────────────
export interface StateRow {
  readonly stateId: string;
  readonly stateCode: string;
  readonly stateName: string;
  readonly countryCode: string;
  readonly gstStateCode: string | null;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
  readonly recordStatusCode: string;
}
export interface CreateStateInput {
  stateCode: string;
  stateName: string;
  countryCode?: string;
  effectiveFromDate: string;
}
export const listStates = (token: Tok) => getList<StateRow>(`/v1/states${qs({ limit: 200 })}`, token);
export const getState = (token: Tok, id: string) => apiRequest<StateRow>(`/v1/states/${id}`, { token });
export const createState = (token: Tok, body: CreateStateInput) =>
  apiRequest<StateRow>('/v1/states', { method: 'POST', token, body });
export const updateState = (token: Tok, id: string, body: { stateName?: string; countryCode?: string }) =>
  apiRequest<StateRow>(`/v1/states/${id}`, { method: 'PUT', token, body });

// ── District (T034) ──────────────────────────────────────────────────────────
export interface DistrictRow {
  readonly districtId: string;
  readonly districtCode: string;
  readonly districtName: string;
  readonly stateId: string;
  readonly zoneId: string | null;
  readonly regionId: string | null;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
  readonly recordStatusCode: string;
}
export interface CreateDistrictInput {
  districtCode: string;
  districtName: string;
  stateId: string;
  zoneId?: string;
  regionId?: string;
  effectiveFromDate: string;
}
export const listDistricts = (token: Tok, stateId?: string) =>
  getList<DistrictRow>(`/v1/districts${qs({ state_id: stateId, limit: 1000 })}`, token);
export const getDistrict = (token: Tok, id: string) => apiRequest<DistrictRow>(`/v1/districts/${id}`, { token });
export const createDistrict = (token: Tok, body: CreateDistrictInput) =>
  apiRequest<DistrictRow>('/v1/districts', { method: 'POST', token, body });
export const updateDistrict = (token: Tok, id: string, body: Partial<CreateDistrictInput>) =>
  apiRequest<DistrictRow>(`/v1/districts/${id}`, { method: 'PUT', token, body });
export const deactivateDistrict = (token: Tok, id: string) =>
  apiRequest<DistrictRow>(`/v1/districts/${id}/deactivate`, { method: 'POST', token });

// ── Department (T029) ────────────────────────────────────────────────────────
export interface DeptRow {
  readonly deptId: string;
  readonly deptCode: string;
  readonly deptName: string;
  readonly statusCode?: string;
  readonly recordStatusCode?: string;
  readonly headCount?: number;
  readonly parentDeptId?: string | null;
  readonly effectiveFromDate?: string;
}
export const deptStatusOf = (d: DeptRow): string => d.statusCode ?? d.recordStatusCode ?? '—';
export interface CreateDeptInput {
  deptCode: string;
  deptName: string;
  parentDeptId?: string;
  effectiveFromDate: string;
}
export const listDepts = (token: Tok) => getList<DeptRow>(`/v1/departments${qs({ limit: 200 })}`, token);
export const getDept = (token: Tok, id: string) => apiRequest<DeptRow>(`/v1/departments/${id}`, { token });
export const createDept = (token: Tok, body: CreateDeptInput) =>
  apiRequest<DeptRow>('/v1/departments', { method: 'POST', token, body });
export const updateDept = (token: Tok, id: string, body: Partial<CreateDeptInput>) =>
  apiRequest<DeptRow>(`/v1/departments/${id}`, { method: 'PUT', token, body });
export const deactivateDept = (token: Tok, id: string) =>
  apiRequest<DeptRow>(`/v1/departments/${id}/deactivate`, { method: 'POST', token });

// ── Designation (T030) ───────────────────────────────────────────────────────
export interface DesigRow {
  readonly desigId: string;
  readonly desigCode: string;
  readonly desigName: string;
  readonly level?: number;
  readonly desigLevel?: number;
  readonly deptId?: string;
  readonly grade?: string | null;
  readonly effectiveFromDate?: string;
}
export interface CreateDesigInput {
  desigCode: string;
  desigName: string;
  deptId: string;
  grade?: string;
  desigLevel?: number;
  effectiveFromDate: string;
}
export const listDesigs = (token: Tok, deptId?: string) =>
  getList<DesigRow>(`/v1/designations${qs({ dept_id: deptId, limit: 200 })}`, token);
export const getDesig = (token: Tok, id: string) => apiRequest<DesigRow>(`/v1/designations/${id}`, { token });
export const createDesig = (token: Tok, body: CreateDesigInput) =>
  apiRequest<DesigRow>('/v1/designations', { method: 'POST', token, body });
export const updateDesig = (token: Tok, id: string, body: Partial<CreateDesigInput>) =>
  apiRequest<DesigRow>(`/v1/designations/${id}`, { method: 'PUT', token, body });

// ── Territory (T035) ─────────────────────────────────────────────────────────
export interface TerritoryRow {
  readonly territoryId: string;
  readonly territoryCode: string;
  readonly territoryName: string;
  readonly districtName?: string;
  readonly stateName?: string;
  readonly regionName?: string;
  readonly zoneName?: string;
  readonly districtId?: string;
  readonly stateId?: string;
  readonly zoneId?: string;
  readonly regionId?: string;
  readonly territoryManagerId?: string | null;
}
export interface CreateTerritoryInput {
  territoryCode: string;
  territoryName: string;
  districtId: string;
  stateId: string;
  zoneId: string;
  regionId: string;
  territoryManagerId?: string;
  effectiveFromDate: string;
}
export const listTerritories = (token: Tok, filter?: { zoneId?: string; regionId?: string; districtId?: string; search?: string }) =>
  getList<TerritoryRow>(
    `/v1/territories${qs({ zone_id: filter?.zoneId, region_id: filter?.regionId, district_id: filter?.districtId, search: filter?.search, limit: 300 })}`,
    token,
  );
export const getTerritory = (token: Tok, id: string) => apiRequest<TerritoryRow>(`/v1/territories/${id}`, { token });
export const createTerritory = (token: Tok, body: CreateTerritoryInput) =>
  apiRequest<TerritoryRow>('/v1/territories', { method: 'POST', token, body });
export const updateTerritory = (token: Tok, id: string, body: Partial<CreateTerritoryInput>) =>
  apiRequest<TerritoryRow>(`/v1/territories/${id}`, { method: 'PUT', token, body });
export const deactivateTerritory = (token: Tok, id: string) =>
  apiRequest<TerritoryRow>(`/v1/territories/${id}/deactivate`, { method: 'POST', token });

// ── Employee (T036) ──────────────────────────────────────────────────────────
// The backend list projects the T036 row's ids into names (fullName,
// departmentName, designationName, territoryName) beside the raw entity
// fields, so EmployeeRow is a superset of both; use the `employee*Of` helpers
// to read either shape.
export interface EmployeeRow {
  readonly empExtId: string;
  readonly employeeCode: string;
  // The IAM user behind the employee. Stakeholder and territory maps key on
  // this, not on empExtId.
  readonly userId?: string;
  // Display fields, resolved by the backend from the ids the row carries.
  readonly fullName?: string | null;
  readonly departmentName?: string | null;
  readonly designationName?: string | null;
  readonly statusCode?: string;
  readonly territoryCode?: string | null;
  readonly territoryName?: string | null;
  // Raw T036 entity fields.
  readonly employeeStatus?: string;
  readonly defaultTerritoryId?: string | null;
  readonly deptId?: string | null;
  readonly desigId?: string | null;
}
export const employeeLabelOf = (e: EmployeeRow): string => e.fullName ?? e.employeeCode;
export const employeeStatusOf = (e: EmployeeRow): string => e.statusCode ?? e.employeeStatus ?? '—';
// Never falls back to defaultTerritoryId: a uuid is not a place, and printing
// one told the reader less than showing nothing.
export const employeeTerritoryOf = (e: EmployeeRow): string | null =>
  e.territoryName ?? e.territoryCode ?? null;
export interface CreateEmployeeInput {
  userId: string;
  deptId?: string;
  desigId?: string;
  employeeCode: string;
  grade?: string;
  joiningDate: string;
  defaultTerritoryId?: string;
  officeLocation?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateId?: string;
  pincode?: string;
  emergencyContactName?: string;
  emergencyContactMobile?: string;
  effectiveFromDate: string;
}
export type UpdateEmployeeInput = Partial<Omit<CreateEmployeeInput, 'userId'>> & {
  employeeStatus?: EmployeeStatus;
};
export const listEmployees = (token: Tok, filter?: { status?: string; deptId?: string; search?: string }) =>
  getList<EmployeeRow>(
    `/v1/employees${qs({ status: filter?.status, dept_id: filter?.deptId, search: filter?.search, limit: 300 })}`,
    token,
  );
export const getEmployee = (token: Tok, empExtId: string) =>
  apiRequest<Record<string, unknown>>(`/v1/employees/${empExtId}`, { token });
export const createEmployee = (token: Tok, body: CreateEmployeeInput) =>
  apiRequest<{ empExtId: string }>('/v1/employees', { method: 'POST', token, body });
export const updateEmployee = (token: Tok, empExtId: string, body: UpdateEmployeeInput) =>
  apiRequest<{ empExtId: string }>(`/v1/employees/${empExtId}`, { method: 'PUT', token, body });
export const deactivateEmployee = (token: Tok, empExtId: string) =>
  apiRequest<unknown>(`/v1/employees/${empExtId}/deactivate`, { method: 'POST', token });

// Employee assignment / lifecycle actions
export const assignManager = (
  token: Tok, empExtId: string, body: { managerId: string; reportingType: ReportingType; effectiveFromDate: string },
) => apiRequest<unknown>(`/v1/employees/${empExtId}/assign-manager`, { method: 'POST', token, body });
export const assignEmpTerritory = (
  token: Tok, empExtId: string, body: { territoryId: string; mappingType: MappingType; effectiveFromDate: string },
) => apiRequest<unknown>(`/v1/employees/${empExtId}/assign-territory`, { method: 'POST', token, body });
export const assignEmpRole = (
  token: Tok, empExtId: string, body: { roleId: string; roleType: RoleType; effectiveFromDate: string; effectiveToDate?: string },
) => apiRequest<unknown>(`/v1/employees/${empExtId}/assign-role`, { method: 'POST', token, body });
export const assignSuccessor = (
  token: Tok, empExtId: string, body: { successorEmployeeId: string; reason: SuccessorReason; effectiveDate: string; endDate?: string; isPermanent?: boolean },
) => apiRequest<unknown>(`/v1/employees/${empExtId}/assign-successor`, { method: 'POST', token, body });
export const transitionEmpStatus = (
  token: Tok, empExtId: string, body: { toStatus: EmployeeStatus; reason?: string },
) => apiRequest<unknown>(`/v1/employees/${empExtId}/status`, { method: 'PATCH', token, body });
export const getSuccessors = (token: Tok, empExtId: string) =>
  getList<Record<string, unknown>>(`/v1/transfers/successors/${empExtId}`, token);

// ── Reporting (T037) ─────────────────────────────────────────────────────────
export interface ReportingNode {
  readonly employeeId: string;
  readonly managerId: string;
  readonly depth: number;
}
export interface ReportingHierarchy {
  readonly root?: string;
  readonly asOf?: string;
  readonly nodes: ReportingNode[];
}
export const getReportingChain = (token: Tok, empExtId: string) =>
  apiRequest<string[]>(`/v1/employees/${empExtId}/reporting-chain`, { token });
export const getReportees = (token: Tok, managerId: string) =>
  getList<Record<string, unknown>>(`/v1/reporting/managers/${managerId}/reportees`, token);
export const getReportingHierarchy = (token: Tok, rootEmployeeId: string, depth = 20) =>
  apiRequest<ReportingHierarchy>(
    `/v1/hierarchy/reporting${qs({ root_employee_id: rootEmployeeId, depth })}`,
    { token },
  );
export const changeManager = (
  token: Tok, empExtId: string, body: { managerId: string; reportingType: ReportingType; effectiveFromDate: string },
) => apiRequest<unknown>(`/v1/reporting/employees/${empExtId}/manager`, { method: 'POST', token, body });

// ── Org chart (T044 cache) ───────────────────────────────────────────────────
// GET /v1/org-chart/:chartType returns a cache envelope (or null when no fresh
// cache exists). The tree, if built, lives opaquely inside `chartData`.
export const CHART_TYPES = ['ORGANIZATION', 'DEPARTMENT', 'REPORTING', 'TERRITORY', 'STAKEHOLDER'] as const;
export type ChartType = (typeof CHART_TYPES)[number];
export interface OrgChartNode {
  readonly empExtId?: string;
  readonly id?: string;
  readonly fullName?: string;
  readonly label?: string;
  readonly name?: string;
  readonly employeeCode?: string;
  readonly designationName?: string;
  readonly departmentName?: string;
  readonly children?: OrgChartNode[];
}
export interface OrgChartCache {
  readonly cacheId?: string;
  readonly chartType?: string;
  readonly scopeEntityId?: string | null;
  readonly chartData?: Record<string, unknown> | null;
  readonly generatedAt?: string;
  readonly validUntil?: string;
  readonly isStale?: boolean;
}
export const getOrgChart = (token: Tok, chartType: ChartType, scopeEntityId?: string) =>
  apiRequest<OrgChartCache | null>(`/v1/org-chart/${chartType}${qs({ scope_entity_id: scopeEntityId })}`, { token });

// ── Transfer (T041) ──────────────────────────────────────────────────────────
export interface TransferRow {
  readonly transferId: string;
  readonly employeeId: string;
  readonly transferType: string;
  readonly fromTerritoryId?: string | null;
  readonly toTerritoryId?: string | null;
  readonly fromManagerId?: string | null;
  readonly toManagerId?: string | null;
  readonly fromDeptId?: string | null;
  readonly toDeptId?: string | null;
  readonly transferReason?: string | null;
  readonly effectiveDate?: string;
  readonly approvalStatus?: string;
  readonly recordStatusCode?: string;
}
export interface CreateTransferInput {
  employeeId: string;
  transferType: TransferType;
  fromTerritoryId?: string;
  toTerritoryId?: string;
  fromManagerId?: string;
  toManagerId?: string;
  fromDeptId?: string;
  toDeptId?: string;
  transferReason?: string;
  effectiveDate: string;
}
export const listTransfers = (token: Tok, filter?: { employeeId?: string; status?: string }) =>
  getList<TransferRow>(`/v1/transfers${qs({ employee_id: filter?.employeeId, status: filter?.status, limit: 200 })}`, token);
export const createTransfer = (token: Tok, body: CreateTransferInput) =>
  apiRequest<{ transferId: string }>('/v1/transfers', { method: 'POST', token, body });
export const approveTransfer = (token: Tok, transferId: string) =>
  apiRequest<unknown>(`/v1/transfers/${transferId}/approve`, { method: 'POST', token });
export const rejectTransfer = (token: Tok, transferId: string, reason: string) =>
  apiRequest<unknown>(`/v1/transfers/${transferId}/reject`, { method: 'POST', token, body: { reason } });

// ── Leave replacement (T043) ─────────────────────────────────────────────────
// Endpoints are nested under the transfers controller and are per-employee only.
export interface LeaveReplacementRow {
  readonly leaveRepId: string;
  readonly employeeId: string;
  readonly replacementEmployeeId: string;
  readonly replacementStartDate?: string;
  readonly replacementEndDate?: string;
  readonly reason?: string | null;
  readonly approvalStatus?: string;
  readonly recordStatusCode?: string;
}
export interface CreateLeaveReplacementInput {
  employeeId: string;
  replacementEmployeeId: string;
  replacementStartDate: string;
  replacementEndDate: string;
  reason?: string;
}
export const listLeaveReplacementsByEmployee = (token: Tok, employeeId: string) =>
  getList<LeaveReplacementRow>(`/v1/transfers/leave-replacements/${employeeId}${qs({ limit: 200 })}`, token);
export const createLeaveReplacement = (token: Tok, body: CreateLeaveReplacementInput) =>
  apiRequest<{ id: string }>('/v1/transfers/leave-replacements', { method: 'POST', token, body });
export const approveLeaveReplacement = (token: Tok, id: string) =>
  apiRequest<unknown>(`/v1/transfers/leave-replacements/${id}/approve`, { method: 'POST', token });

// ── Stakeholder (T040) ───────────────────────────────────────────────────────
// Global upsert lives at POST /v1/stakeholders; listing is per-territory only.
export interface StakeholderRow {
  readonly stakeholderId: string;
  readonly territoryId: string;
  readonly roleType: StakeholderRoleType | string;
  readonly employeeId: string;
  readonly isPrimary?: boolean;
  readonly isBackup?: boolean;
  readonly effectiveFromDate?: string;
  readonly effectiveToDate?: string;
  readonly recordStatusCode?: string;
}
export interface UpsertStakeholderInput {
  territoryId: string;
  roleType: StakeholderRoleType;
  employeeId: string;
  isPrimary: boolean;
  isBackup: boolean;
  effectiveFromDate: string;
}
export const listStakeholdersByTerritory = (token: Tok, territoryId: string) =>
  getList<StakeholderRow>(`/v1/stakeholders/territory/${territoryId}${qs({ limit: 200 })}`, token);
export const upsertStakeholder = (token: Tok, body: UpsertStakeholderInput) =>
  apiRequest<unknown>('/v1/stakeholders', { method: 'POST', token, body });
export const deactivateStakeholder = (token: Tok, stakeholderId: string) =>
  apiRequest<unknown>(`/v1/stakeholders/${stakeholderId}/deactivate`, { method: 'POST', token });

export interface CityRow {
  readonly cityId: string;
  readonly cityCode: string;
  readonly cityName: string;
  readonly pincode: string | null;
  readonly tehsilName: string | null;
}
export const listCitiesByDistrict = (token: Tok, districtId: string) =>
  apiRequest<readonly CityRow[]>(`/v1/districts/${districtId}/cities`, { token });

/** Tehsil is the sub-district level. */
export interface TehsilRow {
  readonly tehsilId: string;
  readonly tehsilCode: string;
  readonly tehsilName: string;
}
export const listTehsilsByDistrict = (token: Tok, districtId: string) =>
  apiRequest<readonly TehsilRow[]>(`/v1/districts/${districtId}/tehsils`, { token });

/* ── Geography scope (per user) ───────────────────────────────────────────── */

export type GeoScopeType = 'ALL_INDIA' | 'STATE' | 'DISTRICT' | 'SUBDISTRICT';
export type GeoScopeOwnerType = 'USER' | 'VENDOR';

export interface GeoScopeRow {
  readonly scopeId: string;
  readonly scopeType: GeoScopeType;
  readonly stateId: string | null;
  readonly stateName: string | null;
  readonly districtId: string | null;
  readonly districtName: string | null;
  readonly tehsilId: string | null;
  readonly tehsilName: string | null;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string | null;
}

export interface GeoScopeConflict {
  readonly ownerId: string;
  readonly displayName: string;
  readonly roleCode: string;
  readonly scopeType: string;
  readonly placeName: string;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string | null;
}

export interface GeoScopePolicy {
  readonly roleCode: string;
  readonly functionalGroup: string;
  readonly hierarchyLevel: string;
  readonly geographyPolicy: string;
}

export interface AssignGeoScopeBody {
  readonly ownerType: GeoScopeOwnerType;
  readonly ownerId: string;
  readonly scopeType: GeoScopeType;
  readonly stateId?: string | null;
  readonly districtId?: string | null;
  readonly tehsilId?: string | null;
  readonly effectiveFromDate: string;
  readonly effectiveToDate?: string | null;
}

export interface EligibleVendor {
  readonly vendorId: string;
  readonly vendorCode: string;
  readonly vendorName: string;
  readonly vendorStatusCode: string;
}

export const listGeoScopes = (token: Tok, ownerType: GeoScopeOwnerType, ownerId: string) =>
  apiRequest<readonly GeoScopeRow[]>(`/v1/geo-scope/owners/${ownerType}/${ownerId}`, { token });

export const getGeoPolicy = (token: Tok, ownerType: GeoScopeOwnerType, ownerId: string) =>
  apiRequest<GeoScopePolicy | null>(`/v1/geo-scope/owners/${ownerType}/${ownerId}/policy`, { token });

/** Vendors covering a district for a branding type, on a given date. */
export const listEligibleVendors = (
  token: Tok,
  q: { districtId: string; brandingTypeId: string; brandingItemId?: string; onDate?: string },
) =>
  apiRequest<readonly EligibleVendor[]>(
    `/v1/geo-scope/eligible-vendors${qs({
      districtId: q.districtId,
      brandingTypeId: q.brandingTypeId,
      brandingItemId: q.brandingItemId,
      onDate: q.onDate,
    })}`,
    { token },
  );

/** Returns clashing holders without saving, so the form can warn before submit. */
export const checkGeoScope = (token: Tok, body: AssignGeoScopeBody) =>
  apiRequest<{ conflicts: readonly GeoScopeConflict[]; policy: GeoScopePolicy | null }>(
    '/v1/geo-scope/check', { method: 'POST', token, body },
  );

export const assignGeoScope = (token: Tok, body: AssignGeoScopeBody) =>
  apiRequest<GeoScopeRow>('/v1/geo-scope', { method: 'POST', token, body });

export const revokeGeoScope = (token: Tok, scopeId: string) =>
  apiRequest<unknown>(`/v1/geo-scope/${scopeId}`, { method: 'DELETE', token });

// ── Geography policy (t625) ──────────────────────────────────────────────────
export const GEOGRAPHY_POLICIES = ['EXCLUSIVE', 'SHARED', 'INHERITED', 'UNRESTRICTED'] as const;
export const HIERARCHY_LEVELS = ['OFFICER', 'MANAGER', 'NA'] as const;

export interface EffectivePolicyRow {
  readonly policyId: string;
  readonly roleCode: string;
  readonly functionalGroup: string;
  readonly hierarchyLevel: string;
  readonly geographyPolicy: string;
  readonly maxUsersPerTenant: number | null;
  /** GLOBAL until this tenant overrides it. */
  readonly source: 'GLOBAL' | 'TENANT';
}

export interface UpsertPolicyBody {
  readonly functionalGroup: string;
  readonly hierarchyLevel: string;
  readonly geographyPolicy: string;
  readonly maxUsersPerTenant?: number | null;
}

export const listGeoPolicies = (token: Tok) =>
  apiRequest<readonly EffectivePolicyRow[]>('/v1/geo-scope/policies', { token });

export const upsertGeoPolicy = (token: Tok, roleCode: string, body: UpsertPolicyBody) =>
  apiRequest<EffectivePolicyRow>(`/v1/geo-scope/policies/${encodeURIComponent(roleCode)}`, {
    method: 'PUT', token, body,
  });

export const resetGeoPolicy = (token: Tok, roleCode: string) =>
  apiRequest<EffectivePolicyRow | null>(`/v1/geo-scope/policies/${encodeURIComponent(roleCode)}`, {
    method: 'DELETE', token,
  });

// ── Hierarchy membership ─────────────────────────────────────────────────────// A zone is defined by the states it covers, a region by its districts, and a
// territory by the districts it takes from its region.

export interface ZoneStateOption {
  readonly stateId: string;
  readonly stateCode: string;
  readonly stateName: string;
  readonly assigned: boolean;
  readonly assignedToOtherZoneId: string | null;
}
export const listZoneStates = (token: Tok, zoneId: string) =>
  apiRequest<readonly ZoneStateOption[]>(`/v1/zones/${zoneId}/states`, { token });
export const setZoneStates = (token: Tok, zoneId: string, stateIds: readonly string[]) =>
  apiRequest<readonly ZoneStateOption[]>(`/v1/zones/${zoneId}/states`, {
    method: 'PUT', token, body: { stateIds },
  });

export interface RegionDistrictOption {
  readonly districtId: string;
  readonly districtCode: string;
  readonly districtName: string;
  readonly stateId: string;
  readonly assigned: boolean;
  readonly assignedToOtherRegionId: string | null;
}
export const listRegionDistricts = (token: Tok, regionId: string) =>
  apiRequest<readonly RegionDistrictOption[]>(`/v1/regions/${regionId}/districts`, { token });
export const setRegionDistricts = (token: Tok, regionId: string, districtIds: readonly string[]) =>
  apiRequest<readonly RegionDistrictOption[]>(`/v1/regions/${regionId}/districts`, {
    method: 'PUT', token, body: { districtIds },
  });

export interface TerritoryDistrictOption {
  readonly districtId: string;
  readonly districtCode: string;
  readonly districtName: string;
  readonly assigned: boolean;
  readonly assignedToOtherTerritoryId: string | null;
}
export const listTerritoryDistricts = (token: Tok, territoryId: string) =>
  apiRequest<readonly TerritoryDistrictOption[]>(`/v1/territories/${territoryId}/districts`, { token });
export const setTerritoryDistricts = (token: Tok, territoryId: string, districtIds: readonly string[]) =>
  apiRequest<readonly TerritoryDistrictOption[]>(`/v1/territories/${territoryId}/districts`, {
    method: 'PUT', token, body: { districtIds },
  });

// ── Reference lookups shared by forms ────────────────────────────────────────
export interface UserLite {
  readonly userId: string;
  readonly displayName: string;
  readonly employeeCode: string | null;
}
// 200 is the server's ceiling; asking for more returns EX-VAL-01 and leaves
// every user picker permanently empty.
export const listUsersLite = (token: Tok) => getList<UserLite>(`/v1/users${qs({ limit: 200 })}`, token);

export interface CompanyLite {
  readonly id: string;
  readonly companyCode: string;
  readonly companyName: string;
}
export const listCompaniesLite = (token: Tok) => getList<CompanyLite>(`/v1/companies${qs({ limit: 200 })}`, token);
